/**
 * ParticleFX — one-shot particle burst effects.
 *
 * Uses Phaser 3.60+ particle emitter API with dynamically generated textures.
 * Game-agnostic: works with any scene, configurable colors/sizes/physics.
 */

declare const Phaser: any;

// ── Texture helpers ──────────────────────────────────────────────────

const generatedTextures = new Set<string>();

/**
 * Generate a small circular particle texture if it doesn't already exist.
 * Cached by key so multiple calls are safe.
 */
export function ensureParticleTexture(
  scene: any,
  key: string = "particle_dot",
  radius: number = 3,
  color: number = 0xffffff
): string {
  if (generatedTextures.has(key) || scene.textures.exists(key)) {
    generatedTextures.add(key);
    return key;
  }

  const size = radius * 2;
  const g = scene.make.graphics({ add: false });
  g.fillStyle(color, 1);
  g.fillCircle(radius, radius, radius);
  g.generateTexture(key, size, size);
  g.destroy();

  generatedTextures.add(key);
  return key;
}

// ── Burst options ────────────────────────────────────────────────────

export interface BurstOptions {
  /** Number of particles. Default 12. */
  count?: number;
  /** Particle texture key. Default: auto-generated white dot. */
  textureKey?: string;
  /** Particle tint color. Default: 0xffffff. */
  tint?: number;
  /** Particle lifespan in ms. Default 600. */
  lifespan?: number;
  /** Emit speed range [min, max]. Default [60, 140]. */
  speed?: { min: number; max: number };
  /** Gravity Y applied to particles. Default 180. */
  gravityY?: number;
  /** Start scale. Default 1. */
  scaleStart?: number;
  /** End scale. Default 0. */
  scaleEnd?: number;
  /** Start alpha. Default 1. */
  alphaStart?: number;
  /** End alpha. Default 0. */
  alphaEnd?: number;
  /** Depth layer. Default 100 (EFFECTS). */
  depth?: number;
  /** Angle range [min, max] in degrees. Default [0, 360] (all directions). */
  angle?: { min: number; max: number };
}

/**
 * One-shot particle burst at a position. Self-cleans after particles die.
 * Returns the emitter for advanced use.
 */
export function burst(scene: any, x: number, y: number, opts?: BurstOptions): any {
  const count = opts?.count ?? 12;
  const lifespan = opts?.lifespan ?? 600;
  const speed = opts?.speed ?? { min: 60, max: 140 };
  const gravityY = opts?.gravityY ?? 180;
  const scaleStart = opts?.scaleStart ?? 1;
  const scaleEnd = opts?.scaleEnd ?? 0;
  const alphaStart = opts?.alphaStart ?? 1;
  const alphaEnd = opts?.alphaEnd ?? 0;
  const depth = opts?.depth ?? 100;
  const angle = opts?.angle ?? { min: 0, max: 360 };

  // Ensure we have a texture
  const texKey = opts?.textureKey ?? ensureParticleTexture(scene);

  const emitter = scene.add.particles(x, y, texKey, {
    lifespan,
    speed,
    gravityY,
    scale: { start: scaleStart, end: scaleEnd },
    alpha: { start: alphaStart, end: alphaEnd },
    angle,
    emitting: false,
    tint: opts?.tint,
  });

  emitter.setDepth(depth);
  emitter.explode(count);

  // Self-cleanup after all particles die
  scene.time.delayedCall(lifespan + 100, () => {
    emitter.destroy();
  });

  return emitter;
}

// ── Convenience presets ──────────────────────────────────────────────

export interface DeathBurstOptions {
  /** Primary tint. Default 0xff6666 (reddish). */
  tint?: number;
  /** Secondary burst tint. Default 0xffaa44 (orangey). */
  tintSecondary?: number;
  /** Scale multiplier for the whole effect. Default 1. */
  scale?: number;
}

/**
 * Death burst — two overlapping particle bursts for a dramatic effect.
 * A fast inner burst + a slower outer spray.
 */
export function deathBurst(scene: any, x: number, y: number, opts?: DeathBurstOptions): void {
  const scale = opts?.scale ?? 1;
  const tint = opts?.tint ?? 0xff6666;
  const tintSecondary = opts?.tintSecondary ?? 0xffaa44;

  ensureParticleTexture(scene);

  // Inner burst — fast, tight
  burst(scene, x, y, {
    count: Math.round(10 * scale),
    tint,
    lifespan: 400,
    speed: { min: 40, max: 100 },
    gravityY: 120,
    scaleStart: 1.2,
    scaleEnd: 0,
  });

  // Outer spray — wider, slower fade
  burst(scene, x, y, {
    count: Math.round(6 * scale),
    tint: tintSecondary,
    lifespan: 700,
    speed: { min: 80, max: 180 },
    gravityY: 200,
    scaleStart: 0.8,
    scaleEnd: 0,
    alphaEnd: 0.1,
  });
}
